import React from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import CloseIcon from '@material-ui/icons/Close'
import Button from '@material-ui/core/Button'
import Link from 'next/link'
import useIsMobile from '@hooks/useIsMobile'
import { getAccreditationExplainer } from './explainers'
import useStyles from './coach-accreditation-explainer-dialog.styles'

/** Wide accreditation marks that need width caps in the modal crest row. */
const WIDE_CREST_SRC_FRAGMENTS = ['adm-certified']

function isWideCrestSrc(src) {
  return WIDE_CREST_SRC_FRAGMENTS.some((fragment) => src.includes(fragment))
}

function isHopeCrestSrc(src) {
  return src.includes('pga-hope')
}

function crestKey(crest) {
  return crest.src
}

function crestImageClassName(crest, styles, multiCrest) {
  const src = crest.src || ''
  if (isHopeCrestSrc(src)) {
    return `${styles.crestImage} ${styles.crestImageHope}`
  }
  if (isWideCrestSrc(src)) {
    return `${styles.crestImage} ${styles.crestImageWide}`
  }
  const multi = multiCrest ? ` ${styles.crestImageMulti}` : ''
  return `${styles.crestImage}${multi}`
}

function renderCrest(crest, styles, multiCrest) {
  return (
    <img
      src={crest.src}
      alt={crest.alt}
      className={crestImageClassName(crest, styles, multiCrest)}
    />
  )
}

function isExternalHref(href) {
  return /^https?:\/\//i.test(href)
}

/**
 * Slugifies an accreditation `id` into a deterministic DOM id used to
 * wire the dialog title to its `aria-labelledby`. Keeps each instance
 * uniquely identifiable when more than one dialog renders in the same
 * tree (defensive — currently only one renders at a time).
 */
function titleDomId(accreditationId) {
  return `coach-accreditation-explainer-${accreditationId}-title`
}

function CtaButton({
  cta, className, dismissClassName, onClose, onCtaClick,
}) {
  const handleClick = () => {
    onCtaClick?.(cta)
    onClose()
  }
  if (!cta.href) {
    return (
      <Button
        type="button"
        variant="contained"
        className={`${className} ${dismissClassName}`}
        data-testid="explainer-cta"
        onClick={handleClick}
      >
        {cta.label}
      </Button>
    )
  }

  if (isExternalHref(cta.href)) {
    return (
      <Button
        component="a"
        href={cta.href}
        variant="contained"
        color="primary"
        className={className}
        data-testid="explainer-cta"
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
      >
        {cta.label}
      </Button>
    )
  }

  return (
    <Link href={cta.href} passHref legacyBehavior>
      <Button
        component="a"
        variant="contained"
        color="primary"
        className={className}
        data-testid="explainer-cta"
        onClick={handleClick}
      >
        {cta.label}
      </Button>
    </Link>
  )
}

CtaButton.propTypes = {
  cta: PropTypes.shape({
    label: PropTypes.string.isRequired,
    href: PropTypes.string,
  }).isRequired,
  className: PropTypes.string.isRequired,
  dismissClassName: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onCtaClick: PropTypes.func,
}

/**
 * Modal explainer for a single coach accreditation. Controlled
 * externally — pass the clicked accreditation `id` via `accreditationId`
 * and an `onClose` to close. Content is looked up from the editorial
 * map in `./explainers.js`; ids without an entry render nothing, so
 * callers don't need to gate the lookup themselves. Directory search
 * directory search cards, the flagged profile bio
 * membership banner (`FLAG_COACH_PROFILE_UX`), and profile PGA
 * Accreditations (`FLAG_COACH_PROFILE_UX` plus
 * `FLAG_COACH_ACCREDITATIONS`) all open this dialog; do not gate the
 * dialog module on those flags.
 *
 * Chrome: close control floats on the paper (no header/footer strokes).
 * Title sits above the crest row, centered, using the Career Services
 * `h3` Source Serif treatment. The first body paragraph is bold; the
 * rest stay regular weight and left-aligned, with extra horizontal
 * inset vs the crest row. Optional `ctaPrompt` is bold and centered
 * immediately above the CTA, with extra space below the button.
 * A `cta` without `href` is a gray dismiss pill that only closes.
 */
function CoachAccreditationExplainerDialog({
  accreditationId,
  onClose,
  onCtaClick,
}) {
  const styles = useStyles()
  const isMobile = useIsMobile()
  const explainer = getAccreditationExplainer(accreditationId)

  /**
   * Render nothing when the dialog is "closed" (no id) OR when the id
   * has no editorial content. Returning `null` keeps the portal and
   * focus-trap completely unmounted between opens — the parent
   * controls visibility purely by toggling `accreditationId`.
   */
  if (!explainer) return null

  const labelledBy = titleDomId(accreditationId)
  const {
    title, crests = [], body = [], cta, ctaPrompt,
  } = explainer

  return (
    <Dialog
      open
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      aria-labelledby={labelledBy}
      classes={{ paper: styles.paper, paperScrollPaper: styles.paperScrollPaper }}
    >
      <IconButton
        aria-label="Close"
        onClick={onClose}
        className={styles.closeButton}
        size="small"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <DialogContent className={styles.content}>
        <Typography
          variant="h3"
          component="h2"
          id={labelledBy}
          className={styles.titleText}
        >
          {title}
        </Typography>
        {crests.length > 0 && (
          <Box
            component="ul"
            className={`${styles.crests}${crests.length > 1 ? ` ${styles.crestsTight}` : ''}`}
            aria-hidden
          >
            {crests.map((crest) => (
              <Box
                component="li"
                key={crestKey(crest)}
                className={styles.crestItem}
              >
                {renderCrest(crest, styles, crests.length > 1)}
              </Box>
            ))}
          </Box>
        )}
        <Box className={styles.bodyStack} data-testid="explainer-body">
          {body.map((paragraph, idx) => (
            <Typography
              // Paragraph copy is editorial / stable; using the index
              // as a key is safe because the explainer body is never
              // reordered at runtime.
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              variant="body1"
              data-testid={idx === 0 ? 'explainer-body-lead' : undefined}
              className={`${styles.bodyParagraph}${idx === 0 ? ` ${styles.bodyParagraphLead}` : ''}`}
            >
              {paragraph}
            </Typography>
          ))}
        </Box>
      </DialogContent>
      {(cta || ctaPrompt) && (
        <DialogActions className={styles.actions}>
          {ctaPrompt && (
            <Typography
              variant="body1"
              data-testid="explainer-cta-prompt"
              className={styles.ctaPrompt}
            >
              {ctaPrompt}
            </Typography>
          )}
          {cta && (
            <CtaButton
              cta={cta}
              className={styles.cta}
              dismissClassName={styles.ctaDismiss}
              onClose={onClose}
              onCtaClick={onCtaClick}
            />
          )}
        </DialogActions>
      )}
    </Dialog>
  )
}

CoachAccreditationExplainerDialog.propTypes = {
  /**
   * The accreditation id whose explainer should be shown, or `null` /
   * `undefined` to keep the dialog closed. Must match an `id` in the
   * map exported from `./explainers.js`; unknown ids render nothing.
   */
  accreditationId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  /** Called with the clicked `cta` before the dialog closes. */
  onCtaClick: PropTypes.func,
}

CoachAccreditationExplainerDialog.defaultProps = {
  accreditationId: null,
  onCtaClick: undefined,
}

export default CoachAccreditationExplainerDialog
